// R221 — **HER MEMBERS TABLE, AND THE EXTRACTION IS EARNED RATHER THAN ARGUED FOR.**
//
// Roadmap 7's method, proved five times now (R215, R216, R217, R220 and this round): look for the
// DEFECT first, and let the extraction pay for the repair. The defect this round found is one door
// down — `openMember` was the SIXTH read on this dashboard and R216 guarded five — and **this table
// is where that door is spelled out twice**, once as the member's own name and once as *View
// usage*. So the block that had to move is the block the round is about.
//
// **IT TAKES PROPS AND HOLDS NO HOOK, WHICH IS THE WHOLE REACH IT BUYS (R7's entry requirement).**
// Inside an eleven-thousand-line file the only instrument that could ever ask about this table was
// a regex; out here `scripts/r221-member-detail.mjs` CALLS it and reads the tree back, so
// **W11-1's harness toggle, its count sentence and D6-4's two doors are RUN** rather than grepped.
//
// **AND ITS THREE STATES ARE THE REASON A CALL IS BETTER THAN A GREP (R18/R65).** *Still reading*,
// *nothing matched* and *here they are* look identical in source and are three different sentences
// on her screen — and the empty one may not fire while the read is still in flight, or a slow
// members read tells the owner of a house with 2,166 accounts that nobody matched.
import { MemberDoor } from "./MemberDoor";
import { HARNESS_TOGGLE_SHOW, HARNESS_TOGGLE_HIDE, harnessHiddenNote } from "../harnessAccounts.js";

// A type moves with the component that renders it.
export type OwnerMember = {
  id: string; name: string; email: string; joinedAt: string;
  hasRoom: boolean; subscription: string; lastActive: string | null; isHarness?: boolean;
};

export function OwnerMembersTab({
  members, search, showHarness, hiddenHarness, loading,
  onSearch, onToggleHarness, onOpenMember, shortDate,
}: {
  members: OwnerMember[];
  search: string;
  showHarness: boolean;
  hiddenHarness: number;
  loading: boolean;
  onSearch: (next: string) => void;
  onToggleHarness: () => void;
  onOpenMember: (id: string) => void;
  // The date formatter arrives as a PARAMETER rather than being imported here, because this file
  // must stay callable by a `.mjs` gate with no locale in the room (R46's `doFetch` pattern).
  shortDate: (v: string | null) => string;
}) {
  return (
    <>
      <div className="owner-bar">
        <input className="owner-search" value={search} onChange={(e) => onSearch(e.target.value)}
          placeholder="Search by name or email..." aria-label="Search members" />
        {/* W11-1 — the one owner-only door onto the harness rows. They are marked, not deleted: a
            delete has to be repeated after every verification run by whoever remembers, and the
            evidence a run leaves is sometimes the point of the run. */}
        <button type="button" className="button button--ghost" onClick={onToggleHarness}>
          {showHarness ? HARNESS_TOGGLE_HIDE : HARNESS_TOGGLE_SHOW}
        </button>
        <a className="button button--ghost" href="/api/owner/export?what=members">Export CSV</a>
      </div>
      {!showHarness && hiddenHarness > 0 ? <p className="owner-page__note">{harnessHiddenNote(hiddenHarness)}</p> : null}
      {loading ? <p className="owner-page__note">Loading...</p> : null}
      <div className="owner-table-wrap">
        <table className="owner-table">
          <thead>
            <tr><th>Name</th><th>Email</th><th>Joined</th><th>Status</th><th>Last active</th><th /></tr>
          </thead>
          <tbody>
            {/* D6-M4 — every cell carries its own column name, so this table becomes CARDS at 390px
                instead of a sideways scroll with a column hiding off the right edge. */}
            {members.map((m) => (
              <tr key={m.id}>
                {/* D6-4 — the name is the door here too, so the row reads the same way it does on
                    every other tab. "View usage" stays: it is the same door, spelled out. */}
                <td data-label="Name"><MemberDoor id={m.id} name={m.name || "Unknown"} onOpen={onOpenMember} /></td>
                <td className="owner-table__email" data-label="Email">{m.email}</td>
                <td data-label="Joined">{shortDate(m.joinedAt)}</td>
                <td data-label="Status">{m.subscription === "owner" ? "Owner" : m.hasRoom ? "Has a room" : "No room yet"}</td>
                <td data-label="Last active">{shortDate(m.lastActive)}</td>
                <td><button type="button" className="owner-link" onClick={() => onOpenMember(m.id)}>View usage</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* R65/R18 — **THE EMPTY SENTENCE WAITS FOR THE READ.** `!loading &&` is not tidiness: without
          it a members read that is still in flight draws *"No members match that."* over a house
          holding two thousand accounts, which is the same picture as a search that really found
          nobody and is the one thing this row must never say by accident. */}
      {!loading && !members.length ? <p className="owner-page__note">No members match that.</p> : null}
    </>
  );
}
